/**
 * SearchDropdown - Global search dropdown
 *
 * Displays search results and recent searches
 */

import { useState, useEffect, useRef } from 'react';
import { Search, Clock, X, FolderKanban, CheckSquare, Users, FileText, MessageCircle, Clock as ClockIcon } from 'lucide-react';
import { useSearch, useRecentSearches, type SearchHit } from '@business-assistant/shared';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../hooks';

interface SearchDropdownProps {
  onClose: () => void;
}

const ENTITY_ICONS: Record<string, any> = {
  project: FolderKanban,
  task: CheckSquare,
  contact: Users,
  invoice: FileText,
  conversation: MessageCircle,
  timeEntry: ClockIcon,
};

export function SearchDropdown({ onClose }: SearchDropdownProps) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const results = useSearch(query);
  const { searches: recentSearches, save: saveRecent, clear: clearRecent } = useRecentSearches();

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSearch = (searchQuery: string) => {
    setQuery(searchQuery);
    saveRecent(searchQuery);
  };

  const handleResultClick = (result: SearchHit) => {
    // Navigate based on entity type
    const routes: Record<string, string> = {
      project: `/projects?id=${result.id}`,
      task: `/projects?task=${result.id}`,
      contact: `/hub/contacts/${result.id}`,
      invoice: `/accounting/invoices/${result.id}`,
      conversation: `/hub/conversations/${result.id}`,
      timeEntry: `/projects?timeentry=${result.id}`,
    };

    const route = routes[result.type];
    if (route) {
      navigate(route);
      onClose();
    }
  };

  const handleClearRecent = () => {
    clearRecent();
  };

  return (
    <div style={{
      position: 'absolute',
      top: '60px',
      right: '16px',
      width: '400px',
      maxWidth: 'calc(100vw - 32px)',
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
      border: '1px solid #e5e7eb',
      zIndex: 1000,
    }}>
      {/* Header with search input */}
      <div style={{ padding: '16px', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          background: '#f3f4f6',
          borderRadius: '8px',
        }}>
          <Search size={18} style={{ color: '#6b7280' }} />
          <input
            ref={inputRef}
            type="text"
            placeholder={t('search.searchEverything')}
            value={query}
            onChange={(e) => handleSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Escape' && onClose()}
            style={{
              flex: 1,
              border: 'none',
              background: 'transparent',
              outline: 'none',
              fontSize: '14px',
            }}
          />
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: '#6b7280',
            }}
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Results or recent searches */}
      <div style={{
        maxHeight: '400px',
        overflowY: 'auto',
      }}>
        {results.length > 0 ? (
          // Search results
          <div style={{ padding: '8px' }}>
            {results.map((result) => {
              const Icon = ENTITY_ICONS[result.type] || FileText;
              return (
                <div
                  key={result.id}
                  onClick={() => handleResultClick(result)}
                  style={{
                    padding: '12px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    background: '#dbeafe',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <Icon size={18} style={{ color: '#3b82f6' }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: '14px', marginBottom: '2px' }}>
                      {result.title}
                    </div>
                    {result.description && (
                      <div style={{
                        fontSize: '13px',
                        color: '#6b7280',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {result.description}
                      </div>
                    )}
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                      {result.type.charAt(0).toUpperCase() + result.type.slice(1)}
                      {result.matchedFields.length > 0 && ` • ${result.matchedFields.join(', ')}`}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : query.trim().length >= 2 ? (
          // No results
          <div style={{
            padding: '48px 24px',
            textAlign: 'center',
            color: '#6b7280',
          }}>
            <Search size={32} style={{ opacity: 0.3, marginBottom: '8px' }} />
            <div style={{ fontSize: '14px' }}>{t('search.noResultsFor', { query })}</div>
          </div>
        ) : recentSearches.length > 0 ? (
          // Recent searches
          <div style={{ padding: '8px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 12px',
            }}>
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>
                {t('search.recentSearches')}
              </span>
              <button
                onClick={handleClearRecent}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#3b82f6',
                  fontSize: '12px',
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                {t('common.clear')}
              </button>
            </div>
            {recentSearches.map((search, i) => (
              <div
                key={i}
                onClick={() => handleSearch(search)}
                style={{
                  padding: '12px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <Clock size={16} style={{ color: '#6b7280' }} />
                <span style={{ fontSize: '14px' }}>{search}</span>
              </div>
            ))}
          </div>
        ) : (
          // Empty state
          <div style={{
            padding: '48px 24px',
            textAlign: 'center',
            color: '#6b7280',
          }}>
            <Search size={32} style={{ opacity: 0.3, marginBottom: '8px' }} />
            <div style={{ fontSize: '14px' }}>{t('search.startTyping')}</div>
            <div style={{ fontSize: '12px', marginTop: '4px' }}>{t('search.searchHint')}</div>
          </div>
        )}
      </div>
    </div>
  );
}
